import { useEffect, useState, type JSX } from 'react';
import { api } from '../lib/api.ts';
import { live } from '../lib/live.ts';
import { isWaitingStatus, useNow } from '../lib/hooks.ts';
import { upsertSession } from '../lib/session-order.ts';
import type { Session } from '../lib/protocol.ts';
import { SessionContents } from './SessionRow.tsx';

export function BacklogView({
  hidden,
  projectId,
  projectName,
  paneId = 'pane-backlog',
  labelledBy = 'tab-backlog',
}: {
  hidden?: boolean;
  projectId: string | null;
  projectName?: string;
  /** The slide-in copy on Home needs its own id: both panes live in the DOM. */
  paneId?: string;
  labelledBy?: string | null;
}): JSX.Element {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [error, setError] = useState<string | null>(null);
  const visible = projectId ? sessions.filter((s) => s.projectId === projectId && s.backlogged) : [];
  const waiting = visible.some((s) => isWaitingStatus(s.runStatus));
  const now = useNow(waiting);

  useEffect(() => {
    let cancelled = false;
    void api
      .sessions()
      .then((next) => {
        if (!cancelled) setSessions(next);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () =>
      live.onSession((updated) =>
        setSessions((prev) => upsertSession(prev, updated)),
      ),
    [],
  );

  const restore = async (id: string): Promise<void> => {
    try {
      const updated = await api.setSessionBacklogged(id, false);
      setSessions((prev) => upsertSession(prev, updated));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div
      className="list"
      id={paneId}
      role={labelledBy ? 'tabpanel' : 'region'}
      aria-labelledby={labelledBy ?? undefined}
      aria-label={labelledBy ? undefined : 'Backlog'}
      hidden={hidden}
    >
      {error && <div className="note note-error">{error}</div>}
      {visible.length === 0 && !error && (
        <div className="empty">
          <p>No chats in the backlog.</p>
          <p className="empty-hint">
            Swipe a chat on Home and tap Backlog to park it
            {projectName ? ` from ${projectName}` : ''}.
          </p>
        </div>
      )}
      {visible.map((s) => (
        <div key={s.id} className="session-swipe">
          <div className="session">
            <SessionContents session={s} now={now} onRestore={() => void restore(s.id)} />
          </div>
        </div>
      ))}
    </div>
  );
}
